import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  cloutReviewsApi,
  ModeratedReview,
  REJECTION_REASONS,
  RejectionReason,
  ReviewStatus,
} from '../services/cloutReviews';
import { KIND_LABELS } from '../components/reviews/ProviderReviews';
import { getApiErrorMessage } from '../utils/errors';

const TABS: { status: ReviewStatus; label: string }[] = [
  { status: 'pending', label: 'Waiting' },
  { status: 'approved', label: 'Published' },
  { status: 'rejected', label: 'Rejected' },
];

const ReviewCard: React.FC<{
  review: ModeratedReview;
  onDone: (id: number) => void;
}> = ({ review, onDone }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState<RejectionReason | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onDone(review.id);
    } catch (err) {
      setError(getApiErrorMessage(err, 'That didn’t work. Please try again.'));
      setBusy(false);
    }
  };

  return (
    <li className="border border-[#eaeaea] rounded-lg p-4 bg-white">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#767472]">
        <Link to={`/provider/public/${review.provider_id}`} className="text-[#c6a664] underline">
          {review.provider_name}
        </Link>
        <span>
          {review.rating} / 5 from {review.reviewer_name}
        </span>
        <span>{KIND_LABELS[review.kind]}</span>
        {review.reviewer_email && <span>{review.reviewer_email}</span>}
        <span>{new Date(review.created_at).toLocaleString()}</span>
      </div>
      <p className="mt-2 text-sm text-[#1f1f1f] whitespace-pre-wrap">{review.review_text}</p>
      {review.status === 'rejected' && review.rejection_reason && (
        <p className="mt-2 text-xs text-[#767472]">
          Rejected: {REJECTION_REASONS[review.rejection_reason]}
          {review.moderator_note ? `. ${review.moderator_note}` : ''}
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {review.status !== 'approved' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => act(() => cloutReviewsApi.approve(review.id))}
            className="px-3 py-1.5 bg-[#1f1f1f] text-white text-xs font-semibold uppercase disabled:opacity-50"
          >
            {review.status === 'rejected' ? 'Approve after all' : 'Approve'}
          </button>
        )}
        {review.status !== 'rejected' && !rejecting && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setRejecting(true)}
            className="px-3 py-1.5 border border-[#1f1f1f] text-xs font-semibold uppercase disabled:opacity-50"
          >
            {review.status === 'approved' ? 'Take down' : 'Reject'}
          </button>
        )}
      </div>

      {rejecting && (
        <div className="mt-3 space-y-2 text-sm">
          <label className="block">
            <span className="font-semibold">Policy reason</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as RejectionReason)}
              className="block mt-1 border border-[#e5e5ea] rounded px-2 py-1.5"
            >
              <option value="">Choose a reason</option>
              {Object.entries(REJECTION_REASONS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="font-semibold">Note (optional, admins only)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={2000}
              className="block w-full mt-1 border border-[#e5e5ea] rounded px-2 py-1.5"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !reason}
              onClick={() =>
                reason && act(() => cloutReviewsApi.reject(review.id, reason, note.trim()))
              }
              className="px-3 py-1.5 bg-red-700 text-white text-xs font-semibold uppercase disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setRejecting(false)}
              className="px-3 py-1.5 text-xs text-[#767472] hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </li>
  );
};

// The admin queue: every review clients leave waits here until an admin approves it
const AdminReviews: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [status, setStatus] = useState<ReviewStatus>('pending');
  const [reviews, setReviews] = useState<ModeratedReview[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const page = await cloutReviewsApi.listForModeration(status);
      setReviews(page.reviews);
      setTotal(page.total);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load reviews.'));
    }
  }, [status]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 font-['Open_Sans']">
        <p>Only CLOUT admins can moderate reviews.</p>
      </div>
    );
  }

  const handleDone = (id: number) => {
    setReviews((current) => current.filter((r) => r.id !== id));
    setTotal((current) => current - 1);
  };

  return (
    <div className="min-h-screen bg-[#fcfaf8]">
      <div className="max-w-4xl mx-auto px-4 py-8 font-['Open_Sans']">
        <h1 className="font-['Ovo'] text-3xl text-[#1f1f1f] mb-2">Review moderation</h1>
        <p className="text-sm text-[#767472] mb-6">
          Reject a review only when it breaks the review policy, for example because it has health
          details, someone else's personal information, abuse or spam, or the writer isn't a client.
          Never reject a review for being negative.
        </p>

        <div className="flex gap-2 mb-4" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.status}
              type="button"
              role="tab"
              aria-selected={status === tab.status}
              onClick={() => setStatus(tab.status)}
              className={`px-3 py-1.5 text-sm rounded ${
                status === tab.status ? 'bg-[#1f1f1f] text-white' : 'bg-white border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <p role="alert" className="text-red-600 mb-4">
            {error}
          </p>
        )}

        <p className="text-sm text-[#767472] mb-3">
          {total} {total === 1 ? 'review' : 'reviews'}
        </p>
        <ul className="space-y-3">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} onDone={handleDone} />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AdminReviews;
